import React, { useState, useCallback, useEffect, useRef, useContext } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  loadStripe,
  PaymentIntent,
  ConfirmCardPaymentData,
} from '@stripe/stripe-js';
import { Helmet } from 'react-helmet-async';
import SubmitButton from '../components/SubmitButton';
import Button from '../components/Button';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { axiosKK } from '../services/networkRequest';
import errorMessage from '../services/errorMessage';
import { ThreeDSModal } from '../components/ThreeDSModal';
import { useHistory } from 'react-router-dom';
import { mapCountryToCountryCode } from '../utilities/Country';
import { LinkIcon } from '../components/icons/LinkIcon';
import { KlarnaIcon } from '../components/icons/KlarnaIcon';
import { VisaIcon, MastercardIcon, AmexIcon, ApplePayIcon } from '../components/icons/CardBrand';
import { CreditCardIcon } from '@heroicons/react/24/solid';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { AxiosError } from 'axios';
import UserContext from '../contexts/UserContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY!!);
const phoneUtil = PhoneNumberUtil.getInstance();
const emailRegExp =
  /^([^\x00-\x7F]|[a-zA-Z0-9_\-.])+@([^\x00-\x7F]|[a-zA-Z0-9_\-.])+\.([^\x00-\x7F]|[a-zA-Z0-9_\-.]){2,}$/;

const OXFORD_COLLEGES = [
  'All Souls College',
  'Balliol College',
  'Brasenose College',
  'Christ Church',
  'Corpus Christi College',
  'Exeter College',
  'Green Templeton College',
  'Harris Manchester College',
  'Hertford College',
  'Jesus College',
  'Keble College',
  'Kellogg College',
  'Lady Margaret Hall',
  'Linacre College',
  'Lincoln College',
  'Magdalen College',
  'Mansfield College',
  'Merton College',
  'New College',
  'Nuffield College',
  'Oriel College',
  'Pembroke College',
  'Queen\'s College',
  'Regent\'s Park College',
  'Reuben College',
  'St Anne\'s College',
  'St Antony\'s College',
  'St Catherine\'s College',
  'St Cross College',
  'St Edmund Hall',
  'St Hilda\'s College',
  'St Hugh\'s College',
  'St John\'s College',
  'St Peter\'s College',
  'Somerville College',
  'Trinity College',
  'University College',
  'Wadham College',
  'Wolfson College',
  'Worcester College',
  'Wycliffe Hall',
  'Other',
];

interface PackageOption {
  id: string;
  name: string;
  saving: string;
  rrp: string;
  image: string;
  price: number; // in pence
  displayPrice: string;
  boxCount: number;
  features: string[];
}

const PACKAGES: PackageOption[] = [
  {
    id: 'small',
    name: 'Small',
    saving: 'Save £25',
    rrp: '£374',
    image: '/img/oxford-package-small.png',
    price: 34900,
    displayPrice: '£349',
    boxCount: 3,
    features: [
      'Includes storage for Hilary & Trinity vac',
      '3 boxes & packing materials',
      'One-off payment',
      'Hassle-free account management',
    ],
  },
  {
    id: 'medium',
    name: 'Medium',
    saving: 'Save £150',
    rrp: '£549',
    image: '/img/oxford-package-medium.png',
    price: 39900,
    displayPrice: '£399',
    boxCount: 5,
    features: [
      'Includes storage for Hilary & Trinity vac',
      '5 boxes & packing materials',
      'One-off payment',
      'Hassle-free account management',
    ],
  },
  {
    id: 'large',
    name: 'Large',
    saving: 'Save £120',
    rrp: '£719',
    image: '/img/oxford-package-large.png',
    price: 59900,
    displayPrice: '£599',
    boxCount: 7,
    features: [
      'Includes storage for Hilary & Trinity vac',
      '7 boxes & packing materials',
      'One-off payment',
      'Hassle-free account management',
    ],
  },
];

const cardInputStyle = {
  style: {
    base: {
      fontSize: '14px',
      color: '#424770',
      fontWeight: '500',
      fontFamily: 'Montserrat, sans-serif',
      '::placeholder': {
        color: '#9e9e9e',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const cardNumberOptions = {
  ...cardInputStyle,
  placeholder: '1234 5678 9012 3456',
  disableLink: true,
};

const HOW_IT_WORKS_STEPS = [
  {
    title: 'Choose Your Package',
    description: 'Select the package that best fits your storage needs for the academic year.',
    icon: '\u{1F4E6}',
    bgColor: '#f3f4f6',
  },
  {
    title: 'Create Your Account',
    description: 'Quick registration so you can manage your storage booking online.',
    icon: '\u{1F464}',
    bgColor: '#e0e7ff',
  },
  {
    title: 'Secure Payment',
    description: 'One simple payment covers boxes, collection, storage & delivery.',
    icon: '\u{1F4B3}',
    bgColor: '#ddd6fe',
  },
  {
    title: 'We Handle the Rest',
    description: 'Receive your boxes, pack up, and we\'ll collect & store them securely.',
    icon: '\u{1F69A}',
    bgColor: '#fef3c7',
  },
];

const cardCvcOptions = {
  ...cardInputStyle,
  placeholder: 'CVC',
};

const cardExpiryOptions = {
  ...cardInputStyle,
  placeholder: 'MM / YY',
};

function OxfordPackagesInner(): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { user } = useContext(UserContext);

  const [step, setStep] = useState(1);
  const [college, setCollege] = useState('');
  const [selectedPackage, setSelectedPackage] = useState<PackageOption | null>(null);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);

  // Registration / login state
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [regFirstname, setRegFirstname] = useState('');
  const [regLastname, setRegLastname] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regFieldErrors, setRegFieldErrors] = useState<Record<string, string>>({});
  const [registrationError, setRegistrationError] = useState<string>();
  const [isRegistering, setIsRegistering] = useState(false);

  // Mobile carousel state
  const cardsRef = useRef<HTMLDivElement>(null);
  const [activeCardIndex, setActiveCardIndex] = useState(1); // default to "Most Popular" (medium)

  // Payment form state
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);

  // Billing address
  const [billingLine1, setBillingLine1] = useState('');
  const [billingLine2, setBillingLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingPostcode, setBillingPostcode] = useState('');
  const [billingCountry, setBillingCountry] = useState('United Kingdom');

  // T&C checkboxes
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePackingRules, setAgreePackingRules] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [paymentError, setPaymentError] = useState<string>();

  // 3DS
  const [threeDSUrl, setThreeDSUrl] = useState('');
  const [show3DS, setShow3DS] = useState(false);
  const [intentClientSecret, setIntentClientSecret] = useState<string>();

  // Success
  const [success, setSuccess] = useState(false);

  // Klarna
  const [paymentMethodType, setPaymentMethodType] = useState<'card' | 'klarna'>('card');
  const [klarnaEmail, setKlarnaEmail] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);

    // Meta Pixel: Track InitiateCheckout when user reaches payment step
    if (step === 3 && selectedPackage) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Oxford_Package',
        value: selectedPackage.price / 100,
        currency: 'GBP',
        num_items: 1,
      });
    }
  }, [step, selectedPackage]);

  // Listen for 3DS completion
  useEffect(() => {
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL;
    const listener = (event: MessageEvent) => {
      if (
        event.origin === frontendUrl &&
        event.data === '3DS-authentication-complete'
      ) {
        if (intentClientSecret && stripe) {
          stripe
            .retrievePaymentIntent(intentClientSecret)
            .then(({ paymentIntent, error: stripeError }) => {
              if (stripeError) {
                setPaymentError(stripeError.message);
              } else if (paymentIntent?.status === 'succeeded') {
                setShow3DS(false);
                setSuccess(true);
              } else if (paymentIntent?.status === 'requires_payment_method') {
                setPaymentError('Please try another payment method');
              }
            });
        }
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [stripe, intentClientSecret]);

  // Auto-scroll to "Most Popular" card on mobile and track active card
  useEffect(() => {
    const container = cardsRef.current;
    if (!container || step !== 1) return;

    // Auto-scroll to medium (index 1) on mount
    const cards = container.children;
    if (cards.length > 1) {
      const targetCard = cards[1] as HTMLElement;
      // Use requestAnimationFrame to ensure layout is ready
      requestAnimationFrame(() => {
        container.scrollTo({
          left: targetCard.offsetLeft - (container.offsetWidth - targetCard.offsetWidth) / 2,
          behavior: 'smooth',
        });
      });
    }

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const containerWidth = container.offsetWidth;
      // Find which card is closest to center
      let closestIndex = 0;
      let closestDistance = Infinity;
      Array.from(cards).forEach((card, i) => {
        const el = card as HTMLElement;
        const cardCenter = el.offsetLeft + el.offsetWidth / 2;
        const distance = Math.abs(cardCenter - (scrollLeft + containerWidth / 2));
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });
      setActiveCardIndex(closestIndex);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [step]);

  const handleDotClick = (index: number) => {
    const container = cardsRef.current;
    if (!container) return;
    const cards = container.children;
    if (cards[index]) {
      const targetCard = cards[index] as HTMLElement;
      container.scrollTo({
        left: targetCard.offsetLeft - (container.offsetWidth - targetCard.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }
  };

  const handleSelectPackage = (pkg: PackageOption) => {
    setSelectedPackage(pkg);
    // Meta Pixel: Track ViewContent when user selects a package
    window.fbq('track', 'ViewContent', {
      content_name: 'Oxford_Package',
      content_type: 'product',
      value: pkg.price / 100,
      currency: 'GBP',
    });
    setStep(user ? 3 : 2);
  };

  const handleBack = () => {
    if (step === 3) setStep(user ? 1 : 2);
    else if (step === 2) {
      setStep(1);
      setIsLoginMode(false);
      setRegistrationError(undefined);
      setRegFieldErrors({});
    }
  };

  const validateRegistration = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!regFirstname || regFirstname.length < 2) errors.firstname = 'At least 2 characters';
    if (!regLastname || regLastname.length < 2) errors.lastname = 'At least 2 characters';
    if (regEmail.length < 5 || !regEmail.match(emailRegExp)?.includes(regEmail)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!regPassword || regPassword.length < 8) {
      errors.password = 'At least 8 characters';
    } else if (regConfirm !== regPassword) {
      errors.confirm = 'Passwords do not match';
    }
    if (regPhone.length < 9) {
      errors.phone = 'Phone number is too short';
    } else {
      try {
        const ukPhone = phoneUtil.parse(regPhone, 'GB');
        if (!phoneUtil.isValidNumber(ukPhone)) errors.phone = 'Invalid UK phone number';
      } catch {
        errors.phone = 'Invalid phone number';
      }
    }
    return errors;
  };

  const storeAuthFromResponse = (response: { data: { jwt: string; customer?: any; user?: any } }) => {
    const userResponse = response.data.customer || response.data.user;
    localStorage.setItem(
      'user',
      JSON.stringify({ ...userResponse, token: response.data.jwt }),
    );
    axiosKK.defaults.headers.common['Authorization'] =
      'bearer ' + response.data.jwt;
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateRegistration();
    setRegFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setRegistrationError(undefined);
    setIsRegistering(true);

    try {
      const response = await axiosKK.post('/register', {
        firstname: regFirstname,
        lastname: regLastname,
        email: regEmail,
        password: regPassword,
        confirm: regConfirm,
        phoneNumber: regPhone,
      });

      storeAuthFromResponse(response);
      setStep(3);
    } catch (err) {
      const axiosErr = err as AxiosError;
      if (axiosErr.response?.status === 409) {
        setIsLoginMode(true);
        setRegistrationError(
          'An account with this email already exists. Please log in below.',
        );
      } else if (axiosErr.response?.status === 429) {
        setRegistrationError(
          'Too many attempts. Please wait a few minutes and try again.',
        );
      } else {
        errorMessage(setRegistrationError)(err as Error);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegistrationError(undefined);
    setRegFieldErrors({});
    setIsRegistering(true);

    if (!regEmail || !regPassword) {
      setRegFieldErrors({
        ...(regEmail ? {} : { email: 'Email is required' }),
        ...(regPassword ? {} : { password: 'Password is required' }),
      });
      setIsRegistering(false);
      return;
    }

    try {
      const response = await axiosKK.post('/login', {
        email: regEmail,
        password: regPassword,
      });

      storeAuthFromResponse(response);
      setStep(3);
    } catch (err) {
      errorMessage(setRegistrationError)(err as Error);
    } finally {
      setIsRegistering(false);
    }
  };

  const isPaymentFormValid = paymentMethodType === 'card'
    ? (college.length > 0 &&
      cardholderName.length >= 3 &&
      cardNumberComplete &&
      cardCvcComplete &&
      cardExpiryComplete &&
      billingLine1.trim().length > 0 &&
      billingCity.trim().length > 0 &&
      billingPostcode.trim().length > 0 &&
      agreeTerms &&
      agreePackingRules)
    : (college.length > 0 &&
      cardholderName.length >= 3 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(klarnaEmail) &&
      billingLine1.trim().length > 0 &&
      billingCity.trim().length > 0 &&
      billingPostcode.trim().length > 0 &&
      agreeTerms &&
      agreePackingRules);

  const handlePayment = useCallback(async () => {
    if (!stripe || !selectedPackage || submitting) return;

    setSubmitting(true);
    setError(undefined);
    setPaymentError(undefined);

    try {
      // Klarna payment flow
      if (paymentMethodType === 'klarna') {
        const { data } = await axiosKK.post<{
          paymentStatus: string;
          paymentIntentSecret?: string;
          orderId?: number;
        }>('/oxford-packages/order', {
          college,
          packageId: selectedPackage.id,
          packageName: selectedPackage.name,
          packagePrice: selectedPackage.price,
          paymentMethodType: 'klarna',
          billingAddress: {
            line1: billingLine1,
            line2: billingLine2,
            city: billingCity,
            postcode: billingPostcode,
            country: billingCountry,
          },
        });

        const clientSecret = data.paymentIntentSecret;
        if (!clientSecret) {
          setError('Failed to create Klarna payment');
          setSubmitting(false);
          return;
        }

        // Save state for return page
        localStorage.setItem('klarna-oxford-order-id', String(data.orderId || ''));
        localStorage.setItem('klarna-return-route', 'oxford-packages');

        const frontendUrl = process.env.REACT_APP_FRONTEND_URL;
        const returnUrl = `${frontendUrl}/oxford-packages/klarna-return`;

        const { error: klarnaError } = await (stripe as any).confirmKlarnaPayment(clientSecret, {
          payment_method: {
            billing_details: {
              email: klarnaEmail,
              name: cardholderName,
              address: {
                line1: billingLine1,
                line2: billingLine2 || undefined,
                city: billingCity,
                postal_code: billingPostcode,
                country: mapCountryToCountryCode(billingCountry),
              },
            },
          },
          return_url: returnUrl,
        });

        if (klarnaError) {
          setError(klarnaError.message || 'Klarna payment failed');
          setSubmitting(false);
        }
        // If no error, user has been redirected to Klarna
        return;
      }

      // Card payment flow
      if (!elements) return;

      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        setError('Card elements not ready');
        setSubmitting(false);
        return;
      }

      const { paymentMethod, error: stripeError } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumber,
          billing_details: {
            name: cardholderName,
            address: {
              line1: billingLine1,
              line2: billingLine2 || undefined,
              city: billingCity,
              postal_code: billingPostcode,
              country: mapCountryToCountryCode(billingCountry),
            },
          },
        });

      if (stripeError) {
        setError(stripeError.message || 'Payment method creation failed');
        setSubmitting(false);
        return;
      }

      if (!paymentMethod) {
        setError('Unable to retrieve payment method');
        setSubmitting(false);
        return;
      }

      const { data } = await axiosKK.post<{
        paymentStatus: string;
        paymentIntentSecret?: string;
        orderId?: number;
      }>('/oxford-packages/order', {
        college,
        packageId: selectedPackage.id,
        packageName: selectedPackage.name,
        packagePrice: selectedPackage.price,
        paymentMethodId: paymentMethod.id,
        billingAddress: {
          line1: billingLine1,
          line2: billingLine2,
          city: billingCity,
          postcode: billingPostcode,
          country: billingCountry,
        },
      });

      if (
        data.paymentStatus === 'succeeded' ||
        data.paymentStatus === 'none_needed'
      ) {
        setSuccess(true);
      } else if (data.paymentStatus === 'requires_action') {
        if (data.paymentIntentSecret) {
          setIntentClientSecret(data.paymentIntentSecret);
          const frontendUrl = process.env.REACT_APP_FRONTEND_URL;

          const confirmData: ConfirmCardPaymentData = {
            payment_method: { card: cardNumber },
            return_url: `${frontendUrl}/oxford-packages`,
          };

          const { paymentIntent } = await stripe.confirmCardPayment(
            data.paymentIntentSecret,
            confirmData,
            { handleActions: false },
          );

          if (paymentIntent) {
            handleIntent(paymentIntent);
          }
        }
      } else if (data.paymentStatus === 'requires_payment_method') {
        setPaymentError('Please try another payment method');
      }
    } catch (err) {
      errorMessage(setError)(err as Error);
    } finally {
      setSubmitting(false);
    }
  }, [
    stripe,
    elements,
    selectedPackage,
    submitting,
    cardholderName,
    billingLine1,
    billingLine2,
    billingCity,
    billingPostcode,
    billingCountry,
    college,
    paymentMethodType,
    klarnaEmail,
  ]);

  const handleIntent = (intent: PaymentIntent) => {
    const action = intent.next_action;
    if (
      intent.status === 'requires_action' &&
      action?.type === 'redirect_to_url'
    ) {
      const url = action?.redirect_to_url?.url;
      if (url) {
        setThreeDSUrl(url);
        setShow3DS(true);
      }
    } else if (intent.status === 'succeeded') {
      setSuccess(true);
    }
  };

  useEffect(() => {
    if (success) {
      // Meta Pixel: Track Purchase on successful payment
      if (selectedPackage) {
        window.fbq('track', 'Purchase', {
          content_name: 'Oxford_Package',
          content_type: 'product',
          value: selectedPackage.price / 100,
          currency: 'GBP',
          num_items: 1,
        });
      }
      history.push('/dashboard');
    }
  }, [success, history, selectedPackage]);

  return (
    <div className="oxford-packages">
      <Helmet>
        <title>Oxford Storage Packages | Kit Keeper</title>
        <meta name="description" content="Affordable Oxford student storage packages. All-inclusive box storage with collection and delivery for Oxford University students." />
        <meta property="og:title" content="Oxford Storage Packages | Kit Keeper" />
        <meta property="og:description" content="Affordable Oxford student storage packages. All-inclusive box storage with collection and delivery for Oxford University students." />
        <meta property="og:url" content="https://book.kitkeeper.co.uk/oxford-packages" />
        <meta property="og:type" content="website" />
      </Helmet>
      <h2 className="blue-black-title">
        <span>Hilary</span>&nbsp;Oxford Packages
      </h2>

      {/* Value Proposition Bar — "Why choose this?" info */}
      <div className="oxford-packages__benefits-bar">
        <div className="benefit">
          <span className="icon">&#128197;</span>
          <span className="text">One booking for both vacs</span>
        </div>
        <div className="benefit">
          <span className="icon">&#128176;</span>
          <span className="text">Save up to &#163;150</span>
        </div>
        <div className="benefit">
          <span className="icon">&#128230;</span>
          <span className="text">All-inclusive</span>
        </div>
        <div className="benefit">
          <span className="icon">&#128274;</span>
          <span className="text">Guaranteed space</span>
        </div>
        <div className="benefit">
          <span className="icon">&#9989;</span>
          <span className="text">Pay once &amp; done</span>
        </div>
      </div>

      {/* Step indicator */}
      <div className="App-numerical-indicator">
        <div className={`indicator-line ${step >= 1 ? 'active' : ''}`}>
          <span className={step >= 1 ? 'active' : undefined}>1</span>
        </div>
        <div className={`indicator-line ${step >= 2 ? 'active' : ''}`}>
          <span className={step >= 2 ? 'active' : undefined}>2</span>
        </div>
        <div className={`indicator-line ${step >= 3 ? 'active' : ''}`}>
          <span className={step >= 3 ? 'active' : undefined}>3</span>
        </div>
      </div>

      {step === 1 && (
        <div className="oxford-packages__step1">
          {/* Package cards */}
          <div className="oxford-packages__cards" ref={cardsRef}>
            {PACKAGES.map((pkg) => (
              <div
                key={pkg.id}
                className={`oxford-packages__card ${pkg.id === 'medium' ? 'most-popular' : ''}`}
                onClick={() => handleSelectPackage(pkg)}
              >
                {pkg.id === 'medium' && (
                  <div className="oxford-packages__card-badge">Most popular</div>
                )}
                <div className="oxford-packages__card-name">{pkg.name}</div>
                <div className="oxford-packages__card-price">
                  {pkg.displayPrice}
                </div>
                <div className="oxford-packages__card-saving">{pkg.saving}</div>
                <img src={pkg.image} alt={`${pkg.name} package`} />
                <ul className="oxford-packages__card-features">
                  {pkg.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
                <button
                  className="oxford-packages__card-cta"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectPackage(pkg);
                  }}
                >
                  Select
                </button>
              </div>
            ))}
          </div>

          {/* Carousel dot indicators (visible on mobile only) */}
          <div className="oxford-packages__dots">
            {PACKAGES.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`oxford-packages__dot ${activeCardIndex === i ? 'active' : ''}`}
                onClick={() => handleDotClick(i)}
                aria-label={`Go to package ${i + 1}`}
              />
            ))}
          </div>

          {/* How it works button */}
          <div className="oxford-packages__info-row bottom">
            <button
              className="oxford-packages__how-it-works-btn"
              type="button"
              onClick={() => setShowHowItWorksModal(true)}
            >
              How it works
            </button>
          </div>

          {/* How It Works modal */}
          {showHowItWorksModal && (
            <div
              className="oxford-packages__modal-overlay"
              onClick={() => setShowHowItWorksModal(false)}
            >
              <div
                className="oxford-packages__how-it-works-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="oxford-packages__hiw-header">
                  <h2>How It Works</h2>
                  <Button
                    variant="tertiary"
                    size="sm"
                    iconOnly
                    className="oxford-packages__modal-close"
                    aria-label="Close"
                    onClick={() => setShowHowItWorksModal(false)}
                  >
                    <XMarkIcon aria-hidden="true" />
                  </Button>
                </div>
                <div className="oxford-packages__steps">
                  {HOW_IT_WORKS_STEPS.map((s, i) => (
                    <div
                      key={i}
                      className="oxford-packages__step-card"
                      style={{ backgroundColor: s.bgColor }}
                    >
                      <div className="oxford-packages__step-icon">
                        <span>{s.icon}</span>
                        <div className="oxford-packages__step-number">{i + 1}</div>
                      </div>
                      <div className="oxford-packages__step-text">
                        <h4>{s.title}</h4>
                        <p>{s.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="oxford-packages__hiw-footer">
                  <button
                    className="oxford-packages__card-cta"
                    type="button"
                    onClick={() => setShowHowItWorksModal(false)}
                  >
                    Got it!
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {step === 2 && (
        <div className="oxford-packages__step2-register">
          <button
            className="oxford-packages__back-btn"
            type="button"
            onClick={handleBack}
          >
            &larr; Back to Packages
          </button>

          <div className="oxford-packages__register-card">
            <div className="oxford-packages__register-header">
              <h3>{isLoginMode ? 'Welcome Back' : 'Create Your Account'}</h3>
              <p>
                {isLoginMode
                  ? 'Log in to continue with your package booking'
                  : 'Quick registration to manage your storage booking'}
              </p>
            </div>

            {registrationError && (
              <div className="oxford-packages__register-error">
                {registrationError}
              </div>
            )}

            {isLoginMode ? (
              <form
                className="oxford-packages__register-form"
                onSubmit={handleLogin}
              >
                <div className="oxford-packages__register-field">
                  <label htmlFor="login-email">Email</label>
                  <input
                    id="login-email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                  {regFieldErrors.email && (
                    <span className="field-error">{regFieldErrors.email}</span>
                  )}
                </div>

                <div className="oxford-packages__register-field">
                  <label htmlFor="login-password">Password</label>
                  <input
                    id="login-password"
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Enter your password"
                  />
                  {regFieldErrors.password && (
                    <span className="field-error">{regFieldErrors.password}</span>
                  )}
                </div>

                <SubmitButton
                  disabled={isRegistering}
                  isSubmitting={isRegistering}
                >
                  Log In &amp; Continue
                </SubmitButton>
              </form>
            ) : (
              <form
                className="oxford-packages__register-form"
                onSubmit={handleRegistration}
              >
                <div className="oxford-packages__register-row">
                  <div className="oxford-packages__register-field">
                    <label htmlFor="reg-firstname">First Name</label>
                    <input
                      id="reg-firstname"
                      value={regFirstname}
                      onChange={(e) => setRegFirstname(e.target.value)}
                      placeholder="Enter your first name"
                    />
                    {regFieldErrors.firstname && (
                      <span className="field-error">{regFieldErrors.firstname}</span>
                    )}
                  </div>
                  <div className="oxford-packages__register-field">
                    <label htmlFor="reg-lastname">Last Name</label>
                    <input
                      id="reg-lastname"
                      value={regLastname}
                      onChange={(e) => setRegLastname(e.target.value)}
                      placeholder="Enter your last name"
                    />
                    {regFieldErrors.lastname && (
                      <span className="field-error">{regFieldErrors.lastname}</span>
                    )}
                  </div>
                </div>

                <div className="oxford-packages__register-field">
                  <label htmlFor="reg-email">Email</label>
                  <input
                    id="reg-email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                  {regFieldErrors.email && (
                    <span className="field-error">{regFieldErrors.email}</span>
                  )}
                </div>

                <div className="oxford-packages__register-row">
                  <div className="oxford-packages__register-field">
                    <label htmlFor="reg-password">Password</label>
                    <input
                      id="reg-password"
                      type="password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                    />
                    {regFieldErrors.password && (
                      <span className="field-error">{regFieldErrors.password}</span>
                    )}
                  </div>
                  <div className="oxford-packages__register-field">
                    <label htmlFor="reg-confirm">Confirm Password</label>
                    <input
                      id="reg-confirm"
                      type="password"
                      value={regConfirm}
                      onChange={(e) => setRegConfirm(e.target.value)}
                      placeholder="Confirm your password"
                    />
                    {regFieldErrors.confirm && (
                      <span className="field-error">{regFieldErrors.confirm}</span>
                    )}
                  </div>
                </div>

                <div className="oxford-packages__register-field">
                  <label htmlFor="reg-phone">Phone (UK Number)</label>
                  <input
                    id="reg-phone"
                    value={regPhone}
                    onChange={(e) => setRegPhone(e.target.value)}
                    placeholder="07XXX XXXXXX"
                  />
                  {regFieldErrors.phone && (
                    <span className="field-error">{regFieldErrors.phone}</span>
                  )}
                </div>

                <SubmitButton
                  disabled={isRegistering}
                  isSubmitting={isRegistering}
                >
                  Create Account &amp; Continue
                </SubmitButton>
              </form>
            )}

            <div className="oxford-packages__auth-toggle">
              {isLoginMode ? (
                <p>
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(false);
                      setRegistrationError(undefined);
                      setRegFieldErrors({});
                    }}
                  >
                    Register
                  </button>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(true);
                      setRegistrationError(undefined);
                      setRegFieldErrors({});
                    }}
                  >
                    Log in
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="oxford-packages__step3-payment">
          <button
            className="oxford-packages__back-btn"
            type="button"
            onClick={handleBack}
          >
            &larr; Back
          </button>

          <div className="oxford-packages__summary">
            <div className="oxford-packages__summary-row">
              <img
                src={selectedPackage?.image}
                alt={`${selectedPackage?.name} package`}
                className="oxford-packages__summary-img"
              />
              <div className="oxford-packages__summary-details">
                <h3>{selectedPackage?.name} Package &mdash; {selectedPackage?.displayPrice}</h3>
                <p>{selectedPackage?.boxCount} boxes</p>
                <p className="oxford-packages__summary-includes">
                  Includes: boxes, collection, storage &amp; redelivery for Hilary and Trinity vac
                </p>
              </div>
            </div>
          </div>

          <form
            className="oxford-packages__payment-form"
            onSubmit={(e) => {
              e.preventDefault();
              handlePayment();
            }}
          >
            {/* College dropdown */}
            <div className="oxford-packages__college">
              <div className="title-bar">
                <h5>Your College</h5>
              </div>
              <select
                id="oxford-college"
                value={college}
                onChange={(e) => setCollege(e.target.value)}
              >
                <option value="">Select your college...</option>
                {OXFORD_COLLEGES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment container (matches Payment.tsx structure) */}
            <div className="payment-container">
              <div className="payment">
                <div className="payment-method-tabs">
                  <button
                    type="button"
                    className={`payment-method-tab ${paymentMethodType === 'card' ? 'payment-method-tab--active' : ''}`}
                    onClick={() => setPaymentMethodType('card')}
                  >
                    <CreditCardIcon width={18} height={18} /> Card
                  </button>
                  <button
                    type="button"
                    className={`payment-method-tab ${paymentMethodType === 'klarna' ? 'payment-method-tab--active' : ''}`}
                    onClick={() => setPaymentMethodType('klarna')}
                  >
                    <KlarnaIcon width={18} height={18} /> Klarna
                  </button>
                </div>
                <div className="card-info">
                  <div className="title-bar">
                    <h5>{paymentMethodType === 'card' ? 'Card Details' : 'Klarna'}</h5>
                  </div>
                  <div className="accepted-payment-methods">
                    <span className="payment-methods-label">We accept:</span>
                    <div className="payment-icons">
                      <VisaIcon />
                      <MastercardIcon />
                      <AmexIcon />
                      <ApplePayIcon />
                      <LinkIcon />
                      <KlarnaIcon />
                    </div>
                  </div>
                  {paymentMethodType === 'card' ? (
                  <div className="input-bar">
                    <div className="each">
                      <section>
                        <input
                          value={cardholderName}
                          onChange={(e) => setCardholderName(e.target.value)}
                          placeholder="Cardholder Name"
                        />
                      </section>
                      <label className="payment-label">Cardholder Name</label>
                    </div>
                    <div className="each">
                      <section>
                        <CardNumberElement
                          options={cardNumberOptions}
                          onChange={(e) => setCardNumberComplete(e.complete)}
                        />
                      </section>
                      <label className="payment-label">Card Number</label>
                    </div>
                    <div className="each-tgt">
                      <div className="each">
                        <section>
                          <CardCvcElement
                            options={cardCvcOptions}
                            onChange={(e) => setCardCvcComplete(e.complete)}
                          />
                        </section>
                        <label className="payment-label">CVC</label>
                      </div>
                      <div className="each">
                        <section>
                          <CardExpiryElement
                            options={cardExpiryOptions}
                            onChange={(e) => setCardExpiryComplete(e.complete)}
                          />
                        </section>
                        <label className="payment-label">Expiry Date</label>
                      </div>
                    </div>
                  </div>
                  ) : (
                  <div className="klarna-info-section">
                    <div className="each">
                      <section>
                        <input
                          type="email"
                          value={klarnaEmail}
                          onChange={(e) => setKlarnaEmail(e.target.value)}
                          placeholder="your@email.com"
                        />
                      </section>
                      <label className="payment-label">Email Address</label>
                    </div>
                    <div className="each">
                      <section>
                        <input
                          value={cardholderName}
                          onChange={(e) => setCardholderName(e.target.value)}
                          placeholder="Full Name"
                        />
                      </section>
                      <label className="payment-label">Full Name</label>
                    </div>
                    <p className="klarna-redirect-info">
                      You'll be redirected to Klarna to complete your payment securely.
                    </p>
                  </div>
                  )}
                </div>
              </div>

              <div className="address">
                <div className="title-bar">
                  <h5>Billing Address</h5>
                </div>
                <div className="billing-fields">
                  <input
                    placeholder="Address Line 1"
                    value={billingLine1}
                    onChange={(e) => setBillingLine1(e.target.value)}
                    required
                  />
                  <input
                    placeholder="Address Line 2 (optional)"
                    value={billingLine2}
                    onChange={(e) => setBillingLine2(e.target.value)}
                  />
                  <input
                    placeholder="City"
                    value={billingCity}
                    onChange={(e) => setBillingCity(e.target.value)}
                    required
                  />
                  <input
                    placeholder="Postcode"
                    value={billingPostcode}
                    onChange={(e) => setBillingPostcode(e.target.value)}
                    required
                  />
                  <select
                    value={billingCountry}
                    onChange={(e) => setBillingCountry(e.target.value)}
                  >
                    <option value="United Kingdom">United Kingdom</option>
                  </select>
                </div>
              </div>
            </div>

            {/* T&C checkboxes */}
            <span className="terms-and-conditions">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={() => setAgreeTerms(!agreeTerms)}
              />
              <label>
                I Agree to the{' '}
                <a
                  href="https://www.kitkeeper.co.uk/terms-and-conditions/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms & Conditions
                </a>
              </label>
            </span>

            <span className="terms-and-conditions">
              <input
                type="checkbox"
                checked={agreePackingRules}
                onChange={() => setAgreePackingRules(!agreePackingRules)}
              />
              <label>I Agree to the Packing Requirements</label>
            </span>

            {submitting && (
              <h3 className="kkUltraViolet">
                Payment is being processed, please do not refresh the page.
              </h3>
            )}

            {error && (
              <>
                <h3 className="error">Error: </h3>
                <h3 className="error">{error}</h3>
              </>
            )}

            {paymentError && (
              <>
                <h3 className="error">Payment Error: </h3>
                <h3 className="error">{paymentError}</h3>
              </>
            )}

            <SubmitButton
              className="payment-btn"
              disabled={!isPaymentFormValid || submitting}
              isSubmitting={submitting}
            >
              {paymentMethodType === 'klarna' ? 'Continue to Klarna' : 'Confirm Payment'}
            </SubmitButton>
          </form>
        </div>
      )}

      {show3DS && <ThreeDSModal url={threeDSUrl} />}
    </div>
  );
}

function OxfordPackages(): JSX.Element {
  return (
    <Elements stripe={stripePromise}>
      <OxfordPackagesInner />
    </Elements>
  );
}

export default OxfordPackages;
